import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { EncryptionBadge } from "~/components/encryption-badge";
import { Button, ButtonRouter } from "~/components/ui/button";
import { ConfirmationDialog } from "~/components/ui/confirmation-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Modal } from "~/components/ui/modal/modal";
import { RichTextViewer } from "~/components/ui/rich-text-editor";
import { Skeleton } from "~/components/ui/skeleton";
import {
  useUnlockedKey,
  useEncryptOnSave,
  useDecryptedResource,
  useCoachShareTargets,
} from "~/lib/crypto";
import {
  useCategoryById,
  categoryByIdQueryOptions,
  categoryQueryKeys,
  useDeleteCategory,
} from "~/lib/queries/categoryQuery";
import { useGoalsOfLife } from "~/lib/queries/goalOfLifeQuery";
import { useUserSharedStatus } from "~/lib/queries/userSharedQuery";
import { type CategoryFormData } from "~/lib/schemas/category-of-life";
import { updateCategoryFile } from "~/lib/upload-file";

import { CategoryForm } from "./-components/category-form";
import { CategoryGoals } from "./-components/category-goals";
import { goalItems } from "./-components/goal-groups";
import { goalCount } from "./-components/goal-list";
import { useGoalPanels } from "./-components/use-goal-panels";

export const Route = createFileRoute("/_user/life-design/$categoryId")({
  staticData: { title: "Category" },
  loader: async ({ params, context }) => {
    const category = await context.queryClient.ensureQueryData(
      categoryByIdQueryOptions(params.categoryId),
    );
    // Missing / not-yours category is an expected miss — render notFound.
    // getCategoryByIdFn returns null here, so the global Sentry function
    // middleware never sees it. The name is encrypted, so the breadcrumb
    // keeps the static title.
    if (!category) throw notFound();
  },
  notFoundComponent: CategoryNotFound,
  component: RouteComponent,
});

function CategoryNotFound() {
  return (
    <div className="mx-auto max-w-xl py-16 text-center">
      <h1 className="font-heading text-2xl font-medium">Category not found</h1>
      <p className="mt-2 text-gray-600">
        This category doesn't exist anymore or isn't one of yours.
      </p>
      <ButtonRouter
        to="/life-design"
        variant="ghost"
        className="mt-6 text-purple-600 hover:bg-purple-50 hover:text-purple-700"
      >
        Back to Life Design
      </ButtonRouter>
    </div>
  );
}

export function RouteComponent() {
  const { categoryId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showHasGoals, setShowHasGoals] = useState(false);

  const { data: category } = useCategoryById(categoryId);
  const { data: sharedStatus } = useUserSharedStatus();
  const deleteCategory = useDeleteCategory();
  const keyManager = useUnlockedKey();
  const { encryptFields } = useEncryptOnSave(keyManager);
  const coachShareTargets = useCoachShareTargets();

  const goals = useGoalsOfLife();
  const items = useMemo(
    () =>
      goalItems(goals.data ?? []).filter(
        (item) => item.category.id === categoryId,
      ),
    [goals.data, categoryId],
  );
  const { openGoal, openNewGoal, panels } = useGoalPanels({
    items,
    keyManager,
    isSharedWithCoaches: sharedStatus?.isSharedGoalsWithCoach ?? false,
  });

  const { data: decrypted, isDecrypting } = useDecryptedResource({
    resourceType: "categoryOfLife",
    resourceId: category?.id,
    keyManager,
    fields: {
      name: { encrypted: category?.encryptedName },
      shortDescription: { encrypted: category?.encryptedShortDescription },
    },
  });

  const displayName = decrypted.name ?? "Untitled";
  const displayDescription = decrypted.shortDescription;

  const initialData: CategoryFormData = {
    name: decrypted.name ?? "",
    shortDescription: displayDescription || "",
    file: null,
  };

  const handleSubmit = async (data: CategoryFormData) => {
    setIsSubmitting(true);

    try {
      const shouldRemoveImage = data.removeExistingImage && !data.file;
      const keepExistingImage = !data.file && !data.removeExistingImage;

      if (!keyManager) {
        toast.error(
          "Encryption is required. Please unlock your encryption key.",
        );
        setIsSubmitting(false);
        return;
      }

      // Each save makes a new key; the coaches it is shared with get theirs
      // in the same save.
      const encrypted = await encryptFields(
        { name: data.name, shortDescription: data.shortDescription ?? "" },
        await coachShareTargets("vision"),
      );

      await updateCategoryFile(
        {
          id: categoryId,
          encryptedName: encrypted.fields.name,
          encryptedShortDescription: encrypted.fields.shortDescription,
          iv: encrypted.iv,
          keyGrants: encrypted.keyGrants,
          shareCategoryName: category?.hasSharedGoals ? data.name : undefined,
          imageUrl: shouldRemoveImage
            ? null
            : keepExistingImage
              ? category?.imageKey || null
              : undefined,
        },
        data.file || undefined,
      );

      await queryClient.invalidateQueries({ queryKey: categoryQueryKeys.all });
      toast.success("Category updated");
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update category:", error);
      toast.error("Failed to update category. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  // A category with goals cannot be deleted, so the page says so up front.
  // The server refuses too, for goals added elsewhere since the list loaded.
  const handleDeleteClick = () => {
    if (items.length > 0) setShowHasGoals(true);
    else setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteCategory.mutateAsync(categoryId);
      setShowDeleteDialog(false);
      toast.success("Category deleted");
      navigate({ to: "/life-design" });
    } catch (error) {
      console.error("Failed to delete category:", error);
      const message =
        error instanceof Error &&
        error.message.includes("linked to one or more goals")
          ? "This category can't be deleted while it still has goals. Delete or move its goals first."
          : "Failed to delete category. Please try again.";
      toast.error(message);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
  };

  const dialogs = (
    <>
      <ConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete category"
        description="Are you sure you want to delete this category? This action cannot be undone. All information related to this category will be lost."
        confirmText="Delete category"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={deleteCategory.isPending}
      />
      <Modal.Root open={showHasGoals} onOpenChange={setShowHasGoals}>
        <Modal.Panel>
          <Modal.Title>This category still has goals</Modal.Title>
          <Modal.Description>
            {`${displayName} still has ${goalCount(items.length)}. Move ${items.length === 1 ? "it" : "them"} to another category or delete ${items.length === 1 ? "it" : "them"} first.`}
          </Modal.Description>
          <div className="mt-4 flex justify-end">
            <Button variant="primary" onClick={() => setShowHasGoals(false)}>
              OK
            </Button>
          </div>
        </Modal.Panel>
      </Modal.Root>
      {panels}
    </>
  );

  if (isEditing) {
    return (
      <div className="space-y-6">
        <CategoryActions
          onDelete={handleDeleteClick}
          deleteDisabled={goals.isLoading}
        />

        {/* The form reads its values once, on mount, so it waits for the
            decrypted text. */}
        {isDecrypting ? (
          <Skeleton className="h-[500px] w-full rounded-xl" />
        ) : (
          <CategoryForm
            initialData={initialData}
            existingImageUrl={category?.imageUrl}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        )}

        {dialogs}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <CategoryActions
        onDelete={handleDeleteClick}
        deleteDisabled={goals.isLoading}
      />

      {/* Hero section with title */}
      <div className="space-y-4 text-center">
        <h1 className="flex items-center justify-center gap-2 font-heading text-4xl font-medium tracking-tight text-gray-900">
          {isDecrypting ? (
            <span className="inline-block h-10 w-60 animate-pulse rounded bg-gray-100" />
          ) : (
            displayName
          )}
          <EncryptionBadge
            variant={
              sharedStatus?.isSharedOverallViewWithCoach ? "shared" : "user"
            }
          />
        </h1>
        <Button
          variant="ghost"
          disabled={isDecrypting}
          onClick={() => setIsEditing(true)}
          className="text-purple-600 hover:bg-purple-50 hover:text-purple-700"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit category
        </Button>
      </div>

      {/* Full-width image */}
      {category?.imageUrl && (
        <div className="mx-auto prose prose-lg w-full overflow-hidden rounded-2xl">
          <img src={category.imageUrl} alt="" className="h-auto w-full" />
        </div>
      )}

      {/* Rich text content */}
      {isDecrypting ? (
        <div className="mx-auto prose prose-lg">
          <div className="h-4 w-80 animate-pulse rounded bg-gray-100" />
        </div>
      ) : (
        displayDescription && (
          <div className="mx-auto prose prose-lg">
            <RichTextViewer
              content={displayDescription}
              className="text-gray-700"
            />
          </div>
        )
      )}

      <CategoryGoals
        categoryName={isDecrypting ? null : displayName}
        items={items}
        isLoading={goals.isLoading}
        isError={goals.isError}
        onRetry={() => void goals.refetch()}
        keyManager={keyManager}
        showPublicLink
        onOpen={(item) => openGoal(item.goal.id)}
        onAddGoal={() => openNewGoal(categoryId)}
      />

      {dialogs}
    </div>
  );
}

/**
 * The page menu. Delete sits here in both the read and the edit view, and
 * waits for the goals, which decide whether the category can go.
 */
function CategoryActions({
  onDelete,
  deleteDisabled,
}: {
  onDelete: () => void;
  deleteDisabled: boolean;
}) {
  return (
    <div className="flex items-center justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Category actions"
            className="h-10 w-10 rounded-xl"
          >
            <MoreVertical className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem
            disabled={deleteDisabled}
            onClick={onDelete}
            className="cursor-pointer text-red-600 focus:text-red-600"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete category
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
